import { useEffect, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  alterarCampanha, campanhaEditavel, codigoDaCampanha, type CampanhaCompleta, type VerticalDaCampanha,
} from '../../api/cptu';
import {
  CONFORMIDADE, GRANDEZAS, PREMISSAS_PADRAO, premissasEfetivas,
  type ColunaDoMotor, type CondicaoDaHomologacao, type Premissas,
} from '../../cptu/motor';
import {
  FORMULAS_DAS_PREMISSAS, colunasQueSaemDaHomologacao, numeroBr, type FormulaDaPremissa, type GrupoDePremissa,
} from '../../cptu/visual';
import { paraCampo, paraNumero, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { Area, Aviso, CampoNumerico, Dialogo, ErroDaApi, useEstreito } from '../../ui';
import { AcoesBloqueadas, motivoDoBloqueio } from './CampanhaMoldura';
import { FormularioDaJanela } from './FormularioDaJanela';
import {
  excecoesDaVertical, focarOPrimeiro, leiturasDaSessao, premissasParaACampanha, rotaDaVertical, useFocoDeVolta,
} from './interpretacao';
import { MUDA_NA_EDICAO, PREMISSAS_DA_EDICAO, textoDaPremissa } from './SimularPremissas';

/**
 * A aba Premissas da campanha CPTu — onda 313, Tarefa 313.5 (spec 07 §5.1 e §6.1, item 6; a prancheta «4 ·
 * Premissas», com as correções do R14; R10).
 *
 * «Premissas de cálculo» e o selo do padrão Testesolo (quantas a campanha alterou); os seis grupos da prancheta, cada
 * premissa com o valor da campanha, o padrão ao lado quando difere e o ⓘ (a fórmula, a fonte e onde vale —
 * transcritos do motor, `formulas.ts` —, e o que ela faz com a homologação). As de valor exigido (RN-252) dizem,
 * quando saem do valor, quantas grandezas deixam o PDF e o Portal; as que o motor 1.0.0 não usa ficam só para ler. O
 * NA, a cota e o pré-furo são da vertical: o quadro «Por vertical» ao lado (cota, NA, pré-furo e as exceções de cada
 * uma), cada linha leva à vertical. O u₀ não hidrostático (o modo e os pontos) também é da vertical: aqui fica para
 * ler, «muda na Edição de cada vertical», e nenhuma das duas ações o muda (rodada final da 313).
 *
 * «Aplicar e recalcular» e «Voltar ao padrão» pedem o motivo e vão ao `PATCH /cptu/campanhas/:id` com as premissas
 * INTEIRAS a partir do gravado (R10: `{ ...campanha.premissas, ...mudancas }`; o padrão é `{}`, fora o u₀ que a
 * campanha tiver, que fica) — nada do que está gravado some, e as exceções das verticais continuam. A tela de cada
 * vertical recalcula com elas quando abre (R9: a aba não recalcula a campanha inteira). O R16 segue como a 313.4 o
 * fez: o PATCH põe a marca da tela, e a régua conta as premissas da campanha só enquanto elas diferem do padrão.
 *
 * Um campo que não se lê bloqueia «Aplicar e recalcular», dizendo qual (como a Edição). Sair da aba com rascunho (um
 * link da página: as abas, o caminho, a vertical do quadro) pergunta «Sair sem gravar?», no molde da vertical e da
 * dissipação; o F5 e o fechar da aba ficam com o aviso do navegador.
 *
 * Sem `cptu.editar`, ou com a campanha fora de rascunho/devolvida, tudo para ler e as duas ações à vista, bloqueadas
 * com o motivo (P-UI-24). No celular é consulta (R8).
 */

/** Os grupos, na ordem da prancheta. */
const GRUPOS: readonly GrupoDePremissa[] = [
  'Cone e água', 'Peso específico e normalização', 'Argilas', 'Areias', 'Deformabilidade e SPT', 'Dissipação',
];

/** As opções escritas das premissas de escolha. */
const OPCOES: Partial<Record<keyof Premissas, readonly [string, string][]>> = {
  gamaModo: [['robertson2010', 'automático · Robertson & Cabal (2010)'], ['fixo', 'fixo']],
  u0Modo: [['hidrostatico', 'hidrostático a partir do NA'], ['pontos', 'por pontos (não hidrostático)']],
};

/** O raio do cone é em metros no motor e em centímetros na tela (1,83 cm, o do .cpt — spec §5.1). */
const ESCALA_NA_TELA: Partial<Record<keyof Premissas, number>> = { raioCone: 100 };

/** As premissas em que o campo vazio é um valor (null): sem φ' de pico padrão. */
const VAZIO_E_NULO: readonly (keyof Premissas)[] = ['phiPicoPadrao'];

/** Os nomes das zonas na lista do que sai da homologação. */
const NOME_DA_ZONA: Partial<Record<ColunaDoMotor, string>> = {
  sbt: 'SBT', sbtn: 'SBTn', sbtnMod: 'SBTn modificado', schneider: 'Schneider',
};

const nomeDaColuna = (c: ColunaDoMotor): string =>
  NOME_DA_ZONA[c] ?? (c in GRANDEZAS ? GRANDEZAS[c as keyof typeof GRANDEZAS].rotulo : c);

/** A condição de homologação de uma premissa (RN-252), se houver. */
const condicaoDe = (k: keyof Premissas): CondicaoDaHomologacao | undefined =>
  CONFORMIDADE.condicoes.find((x) => x.premissa === k);

/**
 * As grandezas homologadas que saem do PDF e do Portal com a premissa neste valor (só ela; as outras no padrão) — a
 * conta única do visual (`colunasQueSaemDaHomologacao`), a mesma do «Simular» da vertical (rodada da 313.5).
 */
function grandezasQueSaem(k: keyof Premissas, valor: unknown): string[] {
  return colunasQueSaemDaHomologacao({ [k]: valor } as Partial<Premissas>).map(nomeDaColuna);
}

/**
 * Um valor fora da condição, para o ⓘ dizer o que sai «com outro valor»: o contrário do sim/não, a outra opção da
 * escolha, o corte de Cn ligado (1,70), o número seguinte.
 */
function valorForaDaCondicao(c: CondicaoDaHomologacao): unknown {
  if (typeof c.valor === 'boolean') return !c.valor;
  if (c.valor === null) return 1.7;
  if (typeof c.valor === 'number') return c.valor + 2;
  return OPCOES[c.premissa]?.find(([v]) => v !== c.valor)?.[0] ?? `${c.valor} (outro)`;
}

/** O valor exigido não está valendo (a premissa saiu da condição da homologação). */
const foraDaCondicao = (c: CondicaoDaHomologacao | undefined, valor: unknown): boolean =>
  !!c && c.efeito === 'valor_exigido' && c.afeta.length > 0 && JSON.stringify(valor ?? null) !== JSON.stringify(c.valor);

/** O rótulo do campo: «φ'cv (°)», «Média móvel (leituras)», «Raio do cone, r (cm)». */
function rotuloDoCampo(f: FormulaDaPremissa): string {
  if (f.chave === 'raioCone') return `${f.rotulo} (cm)`;
  return f.unidade && f.unidade !== 'm × kPa' ? `${f.rotulo} (${f.unidade})` : f.rotulo;
}

/** O que a premissa faz com a homologação, no ⓘ. */
function oQueAfeta(f: FormulaDaPremissa): string {
  const c = condicaoDe(f.chave);
  if (f.naTela === 'so_leitura') return `Sem efeito no motor 1.0.0. ${c?.motivo ?? ''}`.trim();
  if (f.daVertical) return 'É da vertical (o cabeçalho): cada vertical tem o seu, no quadro «Por vertical».';
  if (c && c.efeito === 'valor_exigido') {
    const saem = grandezasQueSaem(c.premissa, valorForaDaCondicao(c));
    if (saem.length === 0) return c.motivo;
    return `Condição da homologação: só com ${textoDaPremissa(f.chave, c.valor)}. Com outro valor, `
      + `${plural(saem.length, 'grandeza sai', 'grandezas saem')} do PDF e do Portal: ${saem.join(', ')}.`;
  }
  return 'Não é condição da homologação: com outro valor as contas mudam, e o que é homologado continua homologado.';
}

/** As exceções de uma vertical por extenso, na ordem das premissas: «Nkt 18 · Ns 6». */
export function textoDasExcecoes(v: VerticalDaCampanha): string | null {
  const excecoes = excecoesDaVertical(v) as Record<string, unknown>;
  const partes = (Object.keys(PREMISSAS_PADRAO) as (keyof Premissas)[])
    .filter((k) => k in excecoes)
    .map((k) => `${FORMULAS_DAS_PREMISSAS[k]?.rotulo ?? k} ${textoDaPremissa(k, excecoes[k])}`);
  return partes.length > 0 ? partes.join(' · ') : null;
}

/** O que o campo numérico lê do texto: o número, null (o vazio que é valor) ou undefined (não se lê). */
function lerOCampo(texto: string, vazioENulo: boolean): number | null | undefined {
  if (texto.trim() === '') return vazioENulo ? null : undefined;
  const n = paraNumero(texto);
  return n !== undefined && Number.isFinite(n) ? n : undefined;
}

/** O u₀ da campanha (o modo e os pontos), que as ações da aba não mudam: «Voltar ao padrão» o mantém. */
function u0DaCampanha(p: Partial<Premissas> | null | undefined): Partial<Premissas> {
  return Object.fromEntries(PREMISSAS_DA_EDICAO.filter((k) => p && k in p).map((k) => [k, p![k]])) as Partial<Premissas>;
}

/** O corte da edição que começa no topo da vertical (o pré-furo cortado do zero), se houver. */
const corteDoTopo = (v: VerticalDaCampanha) => v.edicao?.cortes?.find((k) => k.de <= 0.005) ?? null;

export function CampanhaPremissas({ c, aoMudar }: { c: CampanhaCompleta; aoMudar(): Promise<void> }) {
  const estreito = useEstreito();
  const { pode, eu } = useSessao();
  const navegar = useNavigate();
  const [rascunho, setRascunho] = useState<Partial<Premissas>>({});
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [info, setInfo] = useState<keyof Premissas | null>(null);
  const [janela, setJanela] = useState<'aplicar' | 'padrao' | null>(null);
  const [inicios, setInicios] = useState<Record<string, number>>({});
  /** Para onde a pessoa quis ir com rascunho: a janela «Sair sem gravar?». */
  const [saida, setSaida] = useState<string | null>(null);
  const idDoAplicar = useId();
  const idDoPadrao = useId();
  // As janelas devolvem o foco a quem as abriu (Esc, ✕, «Cancelar»); gravada, ele vai às ações (rodada do agent-browser).
  useFocoDeVolta(info !== null);
  useFocoDeVolta(janela !== null, 'section.cptu-premissas .cptu-premissas-acoes > button');
  useFocoDeVolta(saida !== null);

  const gravadas = useMemo(() => premissasEfetivas(c.campanha.premissas ?? {}), [c.campanha.premissas]);
  const valendo: Premissas = { ...gravadas, ...rascunho };
  const alteradas = Object.keys(c.campanha.premissas ?? {}).filter((k) => k in PREMISSAS_PADRAO).length;
  // O u₀ da campanha não volta ao padrão por aqui (muda na Edição de cada vertical): «Voltar ao padrão» olha o resto.
  const alteradasSemOU0 = alteradas - Object.keys(u0DaCampanha(c.campanha.premissas)).length;
  const sujo = Object.keys(rascunho).length > 0;
  const bloqueio = !pode('cptu.editar') ? 'permissao' : !campanhaEditavel(c.campanha.situacao) ? 'parada' : null;
  const soLer = estreito || !!bloqueio;

  /*
   * Sair da aba com rascunho pede confirmação (rodada final da 313, no molde da vertical e da dissipação): o clique num
   * link interno que leva para fora dela (as abas, o caminho, a vertical do quadro) é segurado antes do roteador e a
   * janela pergunta; o F5 e o fechar da aba ficam com o aviso do navegador.
   */
  useEffect(() => {
    if (!sujo) return undefined;
    const aoClicar = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
      if (!(a instanceof HTMLAnchorElement) || (a.target && a.target !== '_self') || a.hasAttribute('download')) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setSaida(`${url.pathname}${url.search}${url.hash}`);
    };
    const aoSairDaPagina = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('click', aoClicar, true);
    window.addEventListener('beforeunload', aoSairDaPagina);
    return () => {
      window.removeEventListener('click', aoClicar, true);
      window.removeEventListener('beforeunload', aoSairDaPagina);
    };
  }, [sujo]);

  // «início em 2,27 m»: a primeira leitura depois do corte do topo, nas verticais que o têm (as leituras da sessão).
  const verticais = c.verticais.filter((v) => v.situacao === 'ativa');
  const comCorte = verticais.filter((v) => corteDoTopo(v)).map((v) => v.id).join(',');
  useEffect(() => {
    let vale = true;
    for (const v of verticais) {
      const corte = corteDoTopo(v);
      if (!corte) continue;
      void leiturasDaSessao(v.id).then((l) => {
        let z = NaN;
        for (let i = 0; i < l.profundidade.length; i++) {
          if (l.profundidade[i] > corte.ate + 1e-9) { z = l.profundidade[i]; break; }
        }
        if (vale && Number.isFinite(z)) setInicios((x) => ({ ...x, [v.id]: z }));
      }, () => undefined);
    }
    return () => { vale = false; };
    // Só quando muda o conjunto das verticais com corte do topo (a lista muda com a campanha relida).
  }, [comCorte]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Uma premissa mudou: entra no rascunho se difere do gravado; igual a ele, sai. */
  const mudar = (k: keyof Premissas, valor: unknown) => {
    setRascunho((r) => {
      const prox = { ...r } as Record<string, unknown>;
      if (JSON.stringify(valor ?? null) === JSON.stringify(gravadas[k] ?? null)) delete prox[k];
      else prox[k] = valor;
      return prox as Partial<Premissas>;
    });
  };

  /** O campo numérico: o texto como foi digitado; o número que se lê vai ao rascunho. */
  const campoNumerico = (f: FormulaDaPremissa, rotulo: string) => {
    const k = f.chave;
    const escala = ESCALA_NA_TELA[k] ?? 1;
    const vazioENulo = VAZIO_E_NULO.includes(k);
    const v = valendo[k];
    const texto = textos[k] ?? (typeof v === 'number' ? paraCampo(v * escala, 4) : '');
    const erro = lerOCampo(texto, vazioENulo) === undefined ? 'Não se lê: use vírgula para as casas.' : undefined;
    return (
      <CampoNumerico rotulo={rotulo} value={texto} erro={erro} inteiro={k === 'mediaMovel'}
        onChange={(e) => {
          const t = e.target.value;
          setTextos((x) => ({ ...x, [k]: t }));
          const n = lerOCampo(t, vazioENulo);
          if (n === null) mudar(k, null);
          else if (n !== undefined) mudar(k, n / escala);
        }} />
    );
  };

  /*
   * O campo que não se lê fica com o último número que se leu no rascunho: aplicar assim gravaria outra coisa que a
   * tela mostra. Ele bloqueia «Aplicar e recalcular», dizendo qual (como a Edição). Só os campos à vista: o corte de Cn
   * desligado não tem campo.
   */
  const naoSeLeem = (Object.keys(textos) as (keyof Premissas)[])
    .filter((k) => !(k === 'cnCorte' && valendo.cnCorte === null) && !(k === 'gamaFixo' && valendo.gamaModo !== 'fixo'))
    .filter((k) => lerOCampo(textos[k] ?? '', VAZIO_E_NULO.includes(k)) === undefined)
    .map((k) => `«${FORMULAS_DAS_PREMISSAS[k] ? rotuloDoCampo(FORMULAS_DAS_PREMISSAS[k]) : k}» não se lê: use vírgula para as casas`);

  /** A entrada de uma premissa: o campo pelo tipo dela, ou o texto (só para ler). */
  const entradaDe = (f: FormulaDaPremissa): ReactNode => {
    const k = f.chave;
    const rotulo = rotuloDoCampo(f);
    if (f.naTela === 'so_leitura') {
      return (
        <p className="cptu-premissa-leitura">
          <span className="cptu-premissa-rotulo">{rotulo}</span> {textoDaPremissa(k, valendo[k])}
          <span className="cptu-nota"> · sem efeito no motor 1.0.0</span>
        </p>
      );
    }
    if (PREMISSAS_DA_EDICAO.includes(k)) {
      // O u₀ não hidrostático é da vertical: aqui, para ler (rodada final da 313).
      const opcao = OPCOES[k]?.find(([v]) => v === valendo[k]);
      return (
        <p className="cptu-premissa-leitura">
          <span className="cptu-premissa-rotulo">{rotulo}</span> {opcao ? opcao[1] : textoDaPremissa(k, valendo[k])}
          <span className="cptu-nota"> · {MUDA_NA_EDICAO}</span>
        </p>
      );
    }
    if (k === 'gamaFixo' && valendo.gamaModo !== 'fixo') {
      // O γ fixo só conta com o peso específico «fixo»: no automático, para ler, com a nota (rodada do agent-browser).
      return (
        <p className="cptu-premissa-leitura">
          <span className="cptu-premissa-rotulo">{rotulo}</span> {textoDaPremissa(k, valendo[k])}
          <span className="cptu-nota"> · só vale com o peso específico «fixo»</span>
        </p>
      );
    }
    if (soLer) {
      const opcao = OPCOES[k]?.find(([v]) => v === valendo[k]);
      return (
        <p className="cptu-premissa-leitura">
          <span className="cptu-premissa-rotulo">{rotulo}</span> {opcao ? opcao[1] : textoDaPremissa(k, valendo[k])}
        </p>
      );
    }
    if (OPCOES[k]) {
      return (
        <label className="campo">
          <span className="rotulo">{rotulo}</span>
          <select value={String(valendo[k])} onChange={(e) => mudar(k, e.target.value)}>
            {OPCOES[k]!.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
      );
    }
    if (typeof PREMISSAS_PADRAO[k] === 'boolean') {
      return (
        <label className="cptu-caixa-premissa">
          <input type="checkbox" checked={valendo[k] === true} onChange={(e) => mudar(k, e.target.checked)} />
          <span>{rotulo}</span>
        </label>
      );
    }
    if (k === 'cnCorte') {
      const ligado = valendo.cnCorte !== null;
      return (
        <div className="cptu-premissa-composta">
          <label className="cptu-caixa-premissa">
            <input type="checkbox" checked={ligado} onChange={(e) => mudar(k, e.target.checked ? 1.7 : null)} />
            <span>{rotulo} ligado</span>
          </label>
          {ligado && campoNumerico(f, rotulo)}
        </div>
      );
    }
    return campoNumerico(f, rotulo);
  };

  /** Uma linha do quadro: a entrada, o ⓘ, o padrão ao lado, o gravado (no rascunho) e a marca da homologação. */
  const linhaDe = (f: FormulaDaPremissa): ReactNode => {
    const k = f.chave;
    if (f.daVertical && k !== 'na') return null;
    const info = (
      <button type="button" className="cptu-info" aria-label={`A fórmula: ${f.rotulo}`} title="A fórmula e a referência"
        onClick={() => setInfo(k)}>ⓘ</button>
    );
    if (k === 'na') {
      return (
        <div key={k} className="cptu-premissa cptu-premissa-campanha" data-premissa={k}>
          <div className="cptu-premissa-linha">
            <p className="cptu-premissa-leitura">
              <span className="cptu-premissa-rotulo">NA, cota e pré-furo</span> de cada vertical — no quadro por vertical
            </p>
            {info}
          </div>
        </div>
      );
    }
    const difereDoPadrao = JSON.stringify(valendo[k] ?? null) !== JSON.stringify(PREMISSAS_PADRAO[k] ?? null);
    const mudou = k in rascunho;
    const c0 = condicaoDe(k);
    const saem = f.naTela === 'muda' && c0 && foraDaCondicao(c0, valendo[k]) ? grandezasQueSaem(k, valendo[k]) : [];
    const lista = saem.length > 8 ? `${saem.slice(0, 6).join(', ')} e mais ${saem.length - 6}` : saem.join(', ');
    return (
      <div key={k} className={`cptu-premissa cptu-premissa-campanha${mudou ? ' mudou' : ''}`} data-premissa={k}>
        <div className="cptu-premissa-linha">
          {entradaDe(f)}
          {info}
        </div>
        {difereDoPadrao && f.naTela === 'muda' && (
          <p className="cptu-premissa-gravado">padrão {textoDaPremissa(k, PREMISSAS_PADRAO[k])}</p>
        )}
        {mudou && <p className="cptu-premissa-gravado">gravado: {textoDaPremissa(k, gravadas[k])}</p>}
        {saem.length > 0 && (
          <Aviso tipo="atencao" titulo="▲ Fora das condições homologadas">
            {plural(saem.length, 'grandeza sai', 'grandezas saem')} do PDF e do Portal ({lista})
          </Aviso>
        )}
      </div>
    );
  };

  const todas = Object.values(FORMULAS_DAS_PREMISSAS);
  const motivoBloqueado = bloqueio
    ? motivoDoBloqueio(bloqueio, 'não muda as premissas da campanha CPTu', c.campanha.situacao, eu?.atuandoPor ? eu.nome : null)
    : '';
  const noPadrao = alteradasSemOU0 === 0;
  const motivoDoPadrao = !noPadrao ? null : alteradas > 0
    ? 'Fora o u₀, que muda na Edição de cada vertical, a campanha já está no padrão Testesolo.'
    : 'A campanha já está no padrão Testesolo.';
  const motivoDoAplicar = naoSeLeem.length > 0
    ? `Confira antes de aplicar: ${naoSeLeem[0]}${naoSeLeem.length > 1 ? ` (e mais ${naoSeLeem.length - 1})` : ''}.`
    : !sujo ? 'Mude uma premissa para aplicar.' : null;

  return (
    <section className="cptu-premissas" aria-label="Premissas de cálculo">
      <header className="cptu-premissas-cabeca">
        <h2>Premissas de cálculo</h2>
        <span className="cptu-selo-premissas">
          padrão Testesolo · {noPadrao ? 'nenhuma alterada' : plural(alteradasSemOU0, 'alterada', 'alteradas')} nesta campanha
        </span>
        {!estreito && (
          <div className="cptu-premissas-acoes">
            {/* As duas à vista, bloqueadas, com o motivo uma vez só (P-UI-24; rodada do agent-browser). */}
            {bloqueio ? <AcoesBloqueadas textos={['Voltar ao padrão', 'Aplicar e recalcular']} motivo={motivoBloqueado} /> : (
              <>
                {sujo && (
                  <button type="button" className="botao secundario" onClick={() => {
                    setRascunho({});
                    setTextos({});
                    // O botão some: o foco vai à ação que fica (rodada do agent-browser da 313).
                    focarOPrimeiro('section.cptu-premissas .cptu-premissas-acoes > button');
                  }}>
                    Sair sem gravar
                  </button>
                )}
                <button type="button" className="botao secundario" aria-disabled={motivoDoPadrao ? true : undefined}
                  aria-describedby={motivoDoPadrao ? idDoPadrao : undefined}
                  onClick={() => { if (!motivoDoPadrao) setJanela('padrao'); }}>
                  Voltar ao padrão
                </button>
                <button type="button" className="botao" aria-disabled={motivoDoAplicar ? true : undefined}
                  aria-describedby={motivoDoAplicar ? idDoAplicar : undefined}
                  onClick={() => { if (!motivoDoAplicar) setJanela('aplicar'); }}>
                  Aplicar e recalcular
                </button>
                {(motivoDoPadrao || motivoDoAplicar) && (
                  <small className="cptu-motivo-bloqueio">
                    {motivoDoPadrao && <span id={idDoPadrao}>{motivoDoPadrao} </span>}
                    {motivoDoAplicar && <span id={idDoAplicar}>{motivoDoAplicar}</span>}
                  </small>
                )}
              </>
            )}
          </div>
        )}
      </header>
      {estreito && <Aviso tipo="info">Premissas, edição, camadas e dissipações se mudam no computador.</Aviso>}

      <div className="cptu-premissas-corpo">
        <div className="cartao cptu-premissas-grupos">
          {GRUPOS.map((g) => (
            <div key={g} className="cptu-premissas-grupo" role="group" aria-label={g}>
              <h3>{g}</h3>
              {todas.filter((f) => f.grupo === g).map(linhaDe)}
            </div>
          ))}
        </div>
        <aside className="cptu-premissas-lado">
          <div className="cartao cptu-premissas-por-vertical">
            <p className="cptu-premissas-por-vertical-titulo">
              <b>Por vertical</b> <span className="cptu-nota">· o que difere da campanha fica marcado</span>
            </p>
            <div className="rola">
              <table aria-label="Premissas por vertical">
                <thead>
                  <tr>
                    <th scope="col">Vertical</th>
                    <th scope="col" className="n">Cota (m)</th>
                    <th scope="col" className="n">NA (m)</th>
                    <th scope="col">Pré-furo</th>
                    <th scope="col">Exceções</th>
                  </tr>
                </thead>
                <tbody>
                  {verticais.map((v) => {
                    const excecoes = textoDasExcecoes(v);
                    const corte = corteDoTopo(v);
                    const preFuro = [
                      v.pre_furo_m ? `${numeroBr(v.pre_furo_m, 2)} m` : null,
                      corte ? (inicios[v.id] !== undefined
                        ? `início em ${numeroBr(inicios[v.id], 2)} m` : `corte até ${numeroBr(corte.ate, 2)} m`) : null,
                    ].filter(Boolean).join(' · ');
                    return (
                      <tr key={v.id} className={excecoes ? 'cptu-com-excecao' : undefined}>
                        <th scope="row">
                          <Link className="mono" to={rotaDaVertical(c.campanha.id, v.id)}>{v.codigo}</Link>
                        </th>
                        <td className="n mono">
                          {v.cota === null || v.cota === undefined ? '—' : `${v.cota >= 0 ? '+' : ''}${numeroBr(v.cota, 2)}`}
                        </td>
                        <td className="n mono">{v.nivel_agua === null || v.nivel_agua === undefined ? '—' : numeroBr(v.nivel_agua, 2)}</td>
                        <td className="mono">{preFuro || '—'}</td>
                        <td>{excecoes ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="cartao cptu-premissas-nota">
            <p>
              As premissas da campanha valem em toda vertical que não tem exceção. A exceção de uma vertical se grava
              pelo «Simular» na tela dela; o NA e a cota, pelo cabeçalho da vertical.
            </p>
            <p>
              A tela de cada vertical recalcula com as premissas novas quando abre; o «Simular» mostra o antes (cinza) e
              o depois até gravar.
            </p>
          </div>
        </aside>
      </div>

      {info && <JanelaDaFormula f={FORMULAS_DAS_PREMISSAS[info]} aoFechar={() => setInfo(null)} />}
      {janela && (
        <JanelaDasPremissas destino={janela} c={c} gravadas={gravadas} rascunho={rascunho}
          aoFechar={() => setJanela(null)}
          aoPronto={async () => { setJanela(null); setRascunho({}); setTextos({}); await aoMudar(); }} />
      )}
      {saida && (
        <Dialogo aberto titulo="Sair sem gravar?" rotulo={`Campanha ${codigoDaCampanha(c.campanha.sequencia)}`}
          aoFechar={() => setSaida(null)}>
          <p className="sub">
            As premissas que ainda não foram aplicadas se perdem:{' '}
            {(Object.keys(rascunho) as (keyof Premissas)[]).map((k) => FORMULAS_DAS_PREMISSAS[k]?.rotulo ?? k).join(', ')}.
          </p>
          <div className="rodape-dialogo">
            <button type="button" className="botao perigo" onClick={() => {
              const para = saida;
              setSaida(null);
              setRascunho({});
              setTextos({});
              navegar(para);
            }}>Sair sem gravar</button>
            <button type="button" className="botao secundario" onClick={() => setSaida(null)}>Cancelar</button>
          </div>
        </Dialogo>
      )}
    </section>
  );
}

/** O ⓘ de uma premissa: a fórmula, a fonte, onde vale e o que faz com a homologação. */
function JanelaDaFormula({ f, aoFechar }: { f: FormulaDaPremissa; aoFechar(): void }) {
  return (
    <Dialogo aberto titulo={f.rotulo} rotulo="Premissa de cálculo" aoFechar={aoFechar}>
      <div className="cptu-formula-da-janela">
        <p className="mono">{f.formula}</p>
        <p><b>Fonte:</b> {f.referencia}</p>
        {f.aplicabilidade && <p><b>Vale:</b> {f.aplicabilidade}</p>}
        <p><b>O que afeta:</b> {oQueAfeta(f)}</p>
        {f.chave === 'raioCone' && <p>Na tela o raio vai em centímetros; o motor o usa em metros.</p>}
      </div>
    </Dialogo>
  );
}

/**
 * O motivo de «Aplicar e recalcular» e de «Voltar ao padrão» (R10). Aplicar manda as premissas inteiras a partir do
 * gravado; voltar ao padrão manda `{}`. A janela lista o que muda, a exceção das verticais que continua valendo nelas e
 * o que sai da homologação.
 */
function JanelaDasPremissas({ destino, c, gravadas, rascunho, aoFechar, aoPronto }: {
  destino: 'aplicar' | 'padrao';
  c: CampanhaCompleta;
  gravadas: Premissas;
  rascunho: Partial<Premissas>;
  aoFechar(): void;
  aoPronto(): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const rotuloDe = (k: keyof Premissas) => FORMULAS_DAS_PREMISSAS[k]?.rotulo ?? k;
  // «Voltar ao padrão» mantém o u₀ da campanha: ele muda na Edição de cada vertical (rodada final da 313).
  const ficaOU0 = destino === 'padrao' ? u0DaCampanha(c.campanha.premissas) : {};
  const novas: Premissas = destino === 'padrao' ? { ...PREMISSAS_PADRAO, ...ficaOU0 } : { ...gravadas, ...rascunho };
  const chaves = (Object.keys(PREMISSAS_PADRAO) as (keyof Premissas)[])
    .filter((k) => JSON.stringify(gravadas[k] ?? null) !== JSON.stringify(novas[k] ?? null));
  const linhas = chaves.map((k) => `${rotuloDe(k)}: ${textoDaPremissa(k, gravadas[k])} → ${textoDaPremissa(k, novas[k])}`);
  const verticais = c.verticais.filter((v) => v.situacao === 'ativa');
  const continuam = destino === 'padrao'
    ? verticais.map((v) => ({ v, texto: textoDasExcecoes(v) })).filter((x) => x.texto)
    : verticais.flatMap((v) => {
      const excecoes = excecoesDaVertical(v) as Record<string, unknown>;
      return chaves.filter((k) => k in excecoes).map((k) => ({ v, texto: `${rotuloDe(k)} ${textoDaPremissa(k, excecoes[k])}` }));
    });
  const saem = CONFORMIDADE.condicoes
    .filter((x) => foraDaCondicao(x, novas[x.premissa]) && !foraDaCondicao(x, gravadas[x.premissa]))
    .map((x) => ({ rotulo: x.rotulo, n: grandezasQueSaem(x.premissa, novas[x.premissa]).length }))
    .filter((x) => x.n > 0)
    .map((x) => `${x.rotulo} (${plural(x.n, 'grandeza', 'grandezas')})`);
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres): ele vai para a trilha.' : null;

  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      await alterarCampanha(c.campanha.id, {
        premissas: destino === 'padrao' ? ficaOU0 : premissasParaACampanha(c, rascunho),
        motivo: motivo.trim(),
      });
      await aoPronto();
    } catch (e) {
      setErro(e);
      setOcupado(false);
    }
  };

  return (
    <Dialogo aberto titulo={destino === 'padrao' ? 'Voltar ao padrão Testesolo' : 'Aplicar e recalcular'}
      rotulo={`Campanha ${codigoDaCampanha(c.campanha.sequencia)}`} aoFechar={aoFechar}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          {destino === 'padrao'
            ? 'As premissas da campanha voltam ao padrão Testesolo (o do .cpt do 2609350). '
            : 'As premissas da campanha passam a ser estas, inteiras, a partir do que está gravado. '}
          Valem em toda vertical que não tem exceção; a tela de cada vertical recalcula com elas quando abre. As leituras
          não mudam.
        </p>
        <ul className="cptu-lista-da-janela">{linhas.map((l) => <li key={l}>{l}</li>)}</ul>
        {Object.keys(ficaOU0).length > 0 && (
          <Aviso tipo="info">O u₀ da campanha fica como está: ele {MUDA_NA_EDICAO}.</Aviso>
        )}
        {continuam.length > 0 && (
          <Aviso tipo="info"
            titulo={destino === 'padrao' ? 'As exceções das verticais continuam' : 'Nestas verticais continua valendo a exceção'}>
            <ul>{continuam.map((x) => <li key={`${x.v.id}${x.texto}`}>{x.v.codigo}: {x.texto}</li>)}</ul>
          </Aviso>
        )}
        {saem.length > 0 && (
          <Aviso tipo="atencao" titulo="▲ Fora das condições homologadas">
            {saem.join('; ')}. Essas grandezas ficam marcadas «▲ não homologada» na tela e não vão ao PDF nem ao Portal.
          </Aviso>
        )}
        <Area rotulo="Motivo" rows={2} value={motivo} maxLength={2000}
          dica="Vai para a trilha, com quem gravou e quando." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>
            {ocupado ? 'Gravando…' : 'Gravar'}
          </button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}
